import React from 'react';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import SIDEBAR_CONFIG from './sidebarConfig';
import { SET_MENU } from '../../store/actions';
import { hasPermission } from '../../utils';
import { FetchLatestVersion } from 'api/version';
import SidebarHeader from './components/SidebarHeader';
import SidebarNavigation from './components/SidebarNavigation';
import SidebarFooter from './components/SidebarFooter';
import { useLogout } from './hooks';
import {
  DashboardIcon,
  StartupsIcon,
  InvestorsIcon,
  PurchasesIcon,
  SettingsIcon,
} from './components/SidebarIcons';

const NewSidebar = () => {
  const { refetch: refetchLatestVersion } = FetchLatestVersion();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userAccess = useSelector((state) => state?.permissions);
  const details = useSelector((state) => state?.investorDetails?.data);
  const open = useSelector((state) => state.customization.opened);

  // Angel profiles carry a personal photo, organisations a website logo — the
  // same split the profile screen uses to pick a name and an image.
  const isAngel = String(details?.investor_type ?? '')
    .toLowerCase()
    .includes('angel');
  const accountName = isAngel
    ? [details?.first_name, details?.last_name].filter(Boolean).join(' ')
    : details?.organization_name;
  const accountLogo = isAngel ? details?.profile_path : details?.website_logo;

  const toggleDrawer = () => {
    refetchLatestVersion();
    dispatch({ type: SET_MENU, opened: !open });
  };

  const performLogOut = useLogout();

  const handleLogOut = () => {
    performLogOut();
  };

  const menuConfig = buildMenuConfig(userAccess);

  return (
    <>
      <Box
        component="nav"
        aria-label="sidebar navigation"
        sx={{
          width: matchesUpMd
            ? sidebarCollapsed
              ? `${SIDEBAR_CONFIG.desktop.collapsed}px`
              : `${SIDEBAR_CONFIG.desktop.expanded}px`
            : 'auto',
          flexShrink: 0,
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant={matchesUpMd ? 'permanent' : 'temporary'}
          open={matchesUpMd ? false : open}
          onClose={toggleDrawer}
          sx={{
            height: '100dvh',
            '& .MuiDrawer-paper': {
              width: matchesUpMd
                ? sidebarCollapsed
                  ? `${SIDEBAR_CONFIG.desktop.collapsed}px`
                  : `${SIDEBAR_CONFIG.desktop.expanded}px`
                : SIDEBAR_CONFIG.mobile.temporary,
              height: '100%',
              backgroundColor: '#000000',
              color: '#FFFFFF',
              borderRight: 'none',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: 'width 0.3s ease',
              position: matchesUpMd ? 'relative' : 'fixed',
              padding: '0.5rem',
            },
          }}
        >
          <SidebarHeader
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
            navigate={navigate}
          />

          <SidebarNavigation
            menuConfig={menuConfig}
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
          />

          <SidebarFooter
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
            userAccess={userAccess}
            accountName={accountName}
            accountLogo={accountLogo}
            accountLogoUpdatedAt={details?.updatedAt ?? null}
            subtitle={Cookies.get('email')}
            menuConfig={menuConfig}
            handleLogOut={handleLogOut}
          />
        </Drawer>
      </Box>
    </>
  );
};

// Same destinations, permissions and ordering the previous sidebar shipped with —
// only the grouping is new, so the sections can carry the startup app's labels.
function buildMenuConfig(userAccess) {
  return {
    home: [
      {
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
        permission: true,
      },
    ],
    manage: [
      {
        label: 'Startups',
        icon: StartupsIcon,
        path: '/backrr_startups',
        permission: hasPermission(
          'super_admin',
          'backrr_startups',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Investors',
        icon: InvestorsIcon,
        path: '/backrr_investors',
        permission: hasPermission(
          'super_admin',
          'backrr_investors',
          userAccess?.userPermissions
        ),
      },
    ],
    revenue: [
      {
        label: 'Startup Purchases',
        icon: PurchasesIcon,
        path: '/startup_purchases',
        permission: hasPermission(
          'super_admin',
          'startup_purchases',
          userAccess?.userPermissions
        ),
      },
    ],
    bottom: [
      {
        label: 'Admin Settings',
        icon: SettingsIcon,
        path: '/admin_settings',
        permission:
          hasPermission(
            'super_admin',
            'backrr_admin_settings_release_notes',
            userAccess?.userPermissions
          ) ||
          hasPermission(
            'super_admin',
            'backrr_admin_settings_admin_users',
            userAccess?.userPermissions
          ) ||
          hasPermission(
            'super_admin',
            'backrr_admin_settings_promo_codes',
            userAccess?.userPermissions
          ) ||
          hasPermission(
            'super_admin',
            'backrr_admin_settings_plans',
            userAccess?.userPermissions
          ) ||
          hasPermission(
            'super_admin',
            'backrr_admin_settings_profile_score',
            userAccess?.userPermissions
          ) ||
          hasPermission(
            'super_admin',
            'backrr_admin_settings_utm_campaigns',
            userAccess?.userPermissions
          ),
      },
    ],
  };
}

export default NewSidebar;
